import React, { useEffect, useRef } from 'react';
import { User, Building2, Users, Calendar, CalendarCheck, DollarSign, Filter } from 'lucide-react';
import type { CFQuery } from '../../types/campaignFinance';
import CFLookupField from './CFLookupField';
import { useElectionCycles } from '../../hooks/useElectionCycles';
import { useContributorTypes } from '../../hooks/useContributorTypes';
import { useCommitteeTypes } from '../../hooks/useCommitteeTypes';
import { useExpenditureFilterOptions } from '../../hooks/useExpenditureFilterOptions';
import FilterActions from '../FilterActions';

const fmtCycleDate = (s: string): string => {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

interface CFFiltersProps {
  query: CFQuery;
  onChange: (query: CFQuery) => void;
  onSearch: (query: CFQuery) => void;
  onClear: () => void;
  onSaveFilter: () => void;
  className?: string;
}

const labelCls = 'block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1';

const CFFilters: React.FC<CFFiltersProps> = ({ query, onChange, onSearch, onClear, onSaveFilter, className = '' }) => {
  // No default Start Date: defaulting to "Jan 1 this year" silently returns
  // zero results for any candidate/committee whose activity predates the
  // current year. Leave the date filter empty (all dates) unless the user
  // explicitly sets one.
  const startDate = query.start_date ?? '';

  const { cycles } = useElectionCycles();
  const cycleActive = !!query.current_cycle;
  const { options: contributorTypeOptions } = useContributorTypes();
  const { options: committeeTypeOptions } = useCommitteeTypes();
  const { expenditureTypes, disbursementTypes } = useExpenditureFilterOptions();

  const isExpenditures = query.mode === 'expenditures';

  // Switching direction clears the OTHER direction's fields: the two modes are
  // different tables with divergent filter vocabularies, and carrying (say) a
  // contributor_type into an expenditures search would silently filter nothing.
  // Shared fields (committee, candidate, amounts, dates, cycle) survive the switch.
  const setMode = (mode: 'contributions' | 'expenditures') => {
    if ((query.mode || 'contributions') === mode) return;
    if (mode === 'expenditures') {
      onChange({
        ...query, mode,
        contributor: undefined, contributor_type: undefined, contribution_type: undefined, employer: undefined,
      });
    } else {
      onChange({
        ...query, mode: undefined,
        payee: undefined, vendor_id: undefined,
        expenditure_type: undefined, payment_type: undefined, disbursement_type: undefined,
      });
    }
  };

  // The cycle currently applied (matched by its pinned window), or '' for All dates.
  const selectedCycleKey = cycleActive
    ? cycles.findIndex(c => c.startDate === query.start_date && c.endDate === query.end_date)
    : -1;

  const set = (key: keyof CFQuery, value: string) => {
    onChange({ ...query, [key]: value || undefined });
  };

  // Pick a cycle from the dropdown: pin start/end to its window (date inputs go
  // read-only), or clear back to All dates. An explicit "All dates" pick is
  // remembered so the defaulting effect below doesn't immediately undo it.
  const explicitAllDatesRef = useRef(false);
  const selectCycle = (index: number) => {
    const c = cycles[index];
    explicitAllDatesRef.current = !c;
    if (c) {
      onChange({ ...query, current_cycle: true, start_date: c.startDate, end_date: c.endDate });
    } else {
      onChange({ ...query, current_cycle: undefined, start_date: undefined, end_date: undefined });
    }
  };

  // Default the cycle to the most recent (current) window: on first load, and
  // again whenever the form is reset to fully empty (Clear Filters, external
  // resets) — but never overriding an explicit "All dates" choice, and never
  // touching a form that still carries other in-session filters.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (cycles.length === 0 || explicitAllDatesRef.current) return;
    if (query.current_cycle || query.start_date || query.end_date) { defaultedRef.current = true; return; }
    const pristine = Object.entries(query).every(([k, v]) => k === 'scope' || v === undefined || v === '');
    if (defaultedRef.current && !pristine) return;
    defaultedRef.current = true;
    onChange({ ...query, current_cycle: true, start_date: cycles[0].startDate, end_date: cycles[0].endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycles, query]);

  const fmtCycleLabel = (c: { name: string | null; electionYear: number; startDate: string; endDate: string }): string => {
    const base = c.name || `${c.electionYear} cycle`;
    return `${base} (${fmtCycleDate(c.startDate)} – ${fmtCycleDate(c.endDate)})`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(query);
  };

  const hasFilters = Object.entries(query).some(([k, v]) => k !== 'scope' && v !== undefined && v !== '');

  return (
    <div className={`card lg:col-span-2 ${className}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
          Search Campaign Finance Data
        </h3>
        {/* Direction toggle. "Contributions / Expenditures" — deliberately NOT
            "Recipients", which already means contribution-receiving committees on
            this page. Colors reuse the panel's money-in/money-out grammar. */}
        <div role="tablist" aria-label="Search direction" className="inline-flex rounded-[10px] border border-border-light dark:border-border-dark overflow-hidden">
          <button
            type="button"
            role="tab"
            aria-selected={!isExpenditures}
            onClick={() => setMode('contributions')}
            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${!isExpenditures
              ? 'bg-cf-in-soft text-cf-in-text'
              : 'bg-transparent text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary'}`}
          >
            Contributions
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isExpenditures}
            onClick={() => setMode('expenditures')}
            className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-border-light dark:border-border-dark ${isExpenditures
              ? 'bg-cf-out-soft text-cf-out-text'
              : 'bg-transparent text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary'}`}
          >
            Expenditures
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Rows 1–2: identity lookups + type filters (three per row) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {/* Row 1: Candidate · Committee · Committee Type */}
          <div>
            <label className={labelCls}>
              <User className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Candidate Name
            </label>
            <CFLookupField
              endpoint="/campaign-finance/candidate-lookup/options"
              value={query.candidate || ''}
              onChange={v => set('candidate', v)}
              placeholder="Search candidates..."
            />
          </div>
          <div>
            <label className={labelCls}>
              <Building2 className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Committee Name
            </label>
            <CFLookupField
              endpoint="/campaign-finance/committee-lookup/options"
              value={query.committee_name || ''}
              onChange={v => set('committee_name', v)}
              placeholder="Search committees..."
            />
          </div>
          <div>
            <label className={labelCls}>
              <Building2 className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Committee Type
            </label>
            <select
              className="input text-sm"
              value={query.committee_type || ''}
              onChange={e => set('committee_type', e.target.value)}
            >
              <option value="">All Committees</option>
              {committeeTypeOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Row 2 swaps with the direction — the two tables have divergent filter
              vocabularies, so a mixed form is unrepresentable by construction. */}
          {!isExpenditures ? (
            <>
              {/* Contributions: Contributor · Contributor Type · Contribution Type */}
              <div>
                <label className={labelCls}>
                  <Users className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Contributor Name
                </label>
                <CFLookupField
                  endpoint="/campaign-finance/contributor-lookup/options"
                  value={query.contributor || ''}
                  onChange={v => set('contributor', v)}
                  placeholder="Search contributors..."
                />
              </div>
              <div>
                <label className={labelCls}>
                  <Filter className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Contributor Type
                </label>
                <select
                  className="input text-sm"
                  value={query.contributor_type || ''}
                  onChange={e => set('contributor_type', e.target.value)}
                >
                  <option value="">All Contributors</option>
                  {contributorTypeOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>
                  <Filter className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Contribution Type
                </label>
                <select
                  className="input text-sm"
                  value={query.contribution_type || ''}
                  onChange={e => set('contribution_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Monetary">Monetary</option>
                  <option value="Non-Monetary">In-Kind (Non-Monetary)</option>
                  <option value="Returned">Returned / Refunded</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          ) : (
            <>
              {/* Expenditures: Payee · Expenditure Type · Disbursement Type.
                  (payment_type is supported by the API but omitted from the form —
                  check vs card is rarely the analytical question.) */}
              <div>
                <label className={labelCls}>
                  <Users className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Payee Name
                </label>
                <CFLookupField
                  endpoint="/campaign-finance/vendors"
                  value={query.payee || ''}
                  // Free-text edits clear the resolved identity: a vendor_id is only
                  // valid for the exact name it was picked for.
                  onChange={v => onChange({ ...query, payee: v || undefined, vendor_id: undefined })}
                  // A dropdown pick confirms identity — capture the vendor_id.
                  onSelect={opt => onChange({ ...query, payee: opt.value, vendor_id: (opt.meta?.vendor_id as string) || undefined })}
                  transform={(data: any) => (data?.vendors || []).map((v: any) => ({
                    value: v.name,
                    label: v.name,
                    total_amount: v.total,
                    meta: { vendor_id: v.vendor_id },
                  }))}
                  placeholder="Search payees (vendors)..."
                />
              </div>
              <div>
                <label className={labelCls}>
                  <Filter className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Expenditure Type
                </label>
                <select
                  className="input text-sm"
                  value={query.expenditure_type || ''}
                  onChange={e => set('expenditure_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  {expenditureTypes.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>
                  <Filter className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
                  Disbursement Type
                </label>
                <select
                  className="input text-sm"
                  value={query.disbursement_type || ''}
                  onChange={e => set('disbursement_type', e.target.value)}
                >
                  <option value="">All Types</option>
                  {disbursementTypes.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Row 3: money range + date range — compact, four across */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div>
            <label className={labelCls}>
              <DollarSign className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Min Amount
            </label>
            <input
              className="input text-sm"
              type="number"
              min="0"
              step="1"
              placeholder="$0"
              value={query.min_amount || ''}
              onChange={e => set('min_amount', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>
              <DollarSign className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Max Amount
            </label>
            <input
              className="input text-sm"
              type="number"
              min="0"
              step="1"
              placeholder="Any amount"
              value={query.max_amount || ''}
              onChange={e => set('max_amount', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              Start Date
            </label>
            <input
              className="input text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              type="date"
              value={startDate}
              disabled={cycleActive}
              onChange={e => set('start_date', e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 text-text-tertiary dark:text-text-dark-tertiary" />
              End Date
            </label>
            <input
              className="input text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              type="date"
              value={query.end_date || ''}
              disabled={cycleActive}
              onChange={e => set('end_date', e.target.value)}
            />
          </div>
        </div>

        {/* Action row: election-cycle selector on the left, buttons on the right */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5">
          <label className="flex items-center gap-2 text-sm text-text-primary dark:text-text-dark-primary">
            <CalendarCheck className="w-3.5 h-3.5 text-text-tertiary dark:text-text-dark-tertiary flex-none" />
            <span className="font-medium whitespace-nowrap">Election Cycle</span>
            <select
              className="input text-sm py-1.5"
              value={selectedCycleKey}
              disabled={cycles.length === 0}
              onChange={e => selectCycle(Number(e.target.value))}
            >
              <option value={-1}>All dates</option>
              {cycles.map((c, i) => (
                <option key={`${c.electionYear}-${i}`} value={i}>{fmtCycleLabel(c)}</option>
              ))}
            </select>
          </label>

          <FilterActions
            onClear={onClear}
            onSave={onSaveFilter}
            onSearch={() => onSearch(query)}
            hasFilters={hasFilters}
            searchIsSubmit
          />
        </div>
      </form>
    </div>
  );
};

export default CFFilters;
